import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const getToken = () => {
  const storedUser = sessionStorage.getItem("adminkhadamat");
  if (!storedUser) {
    return null;
  }
  try {
    const parsed = JSON.parse(storedUser);
    if (parsed.data?.token?.access_token) {
      // التأكد من أن التوكن هو string
      const token = String(parsed.data.token.access_token);
      return token;
    }
    if (parsed.data?.access_token) {
      return parsed.data.access_token;
    }
    if (parsed.data?.token) {
      return parsed.data.token;
    }
    if (parsed.token) {
      return parsed.token;
    }
    if (parsed.result?.token) {
      return parsed.result.token;
    }
    if (parsed.user?.token) {
      return parsed.user.token;
    }
    return null;
  } catch (error) {
    return null;
  }
};

// Helper function to get current language
const getCurrentLanguage = () => {
  return i18n.language || "ar";
};

// Async Thunk for deleting role
export const deleteRole = createAsyncThunk(
  "auth/deleteRole",
  async (roleId, thunkAPI) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/admin/roles/${roleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          lang: currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "فشل في حذف الصلاحية"
      );
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (userData, { rejectWithValue }) => {
    try {
      const currentLang = getCurrentLanguage();
      const response = await axios.post(`${BASE_URL}/admin/login`, userData, {
        headers: {
          "Accept-Language": currentLang,
          lang: currentLang,
        },
      });
      // إذا كان يحتاج 2FA، نعيد البيانات الخاصة بالـ 2FA
      if (
        response.data?.data?.need_2fa === true ||
        response.data?.need_2fa === true
      ) {
        return {
          need_2fa: true,
          email:
            response.data?.data?.email ||
            response.data?.email ||
            userData.email,
          temp_token:
            response.data?.data?.temp_token ||
            response.data?.temp_token ||
            null,
          ...response.data,
        };
      }
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Login failed!";
      return rejectWithValue({ message });
    }
  }
);

// Enable 2FA
export const enable2FA = createAsyncThunk(
  "auth/enable2FA",
  async (_, { rejectWithValue, getState }) => {
    try {
      const currentLang = getCurrentLanguage();
      let token = null;

      // أولاً: محاولة الحصول على التوكن من adminkhadamat_temp (البيانات من login الناجح)
      const adminTempData = sessionStorage.getItem("adminkhadamat_temp");
      if (adminTempData) {
        try {
          const parsed = JSON.parse(adminTempData);
          // الحصول على access_token من response
          token =
            parsed.data?.token?.access_token ||
            parsed.data?.access_token ||
            parsed.data?.token;
        } catch {}
      }

      // إذا لم نجد، نحاول من adminkhadamat (إذا كان محفوظ)
      if (!token) {
        const adminData = sessionStorage.getItem("adminkhadamat");
        if (adminData) {
          try {
            const parsed = JSON.parse(adminData);
            token =
              parsed.data?.token?.access_token ||
              parsed.data?.access_token ||
              parsed.data?.token;
          } catch {}
        }
      }

      // إذا لم نجد، نحاول استخدام getToken() كحل أخير
      if (!token) {
        token = getToken();
      }

      if (!token) {
        throw new Error("لم يتم العثور على token");
      }

      const headers = {
        "Accept-Language": currentLang,
        lang: currentLang,
        Authorization: `Bearer ${token}`,
      };

      const response = await axios.post(
        `${BASE_URL}/admin/enable-2fa`,
        {},
        {
          headers,
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to enable 2FA!";
      return rejectWithValue({ message });
    }
  }
);

// Verify 2FA
export const verify2FA = createAsyncThunk(
  "auth/verify2FA",
  async ({ email, code, web_fcm_token }, { rejectWithValue }) => {
    try {
      const currentLang = getCurrentLanguage();
      const body = { email, code, web_fcm_token: web_fcm_token || '' };
      const response = await axios.post(
        `${BASE_URL}/admin/verify-2fa`,
        body,
        {
          headers: {
            "Accept-Language": currentLang,
            lang: currentLang,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to verify 2FA code!";
      return rejectWithValue({ message });
    }
  }
);

// Get user profile
export const getUserProfile = createAsyncThunk(
  "auth/getUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/admin/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          lang: currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get profile!";
      return rejectWithValue({ message });
    }
  }
);

// Update user profile
export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/admin/profile`,
        profileData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            lang: currentLang,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update profile!";
      return rejectWithValue({ message });
    }
  }
);

// Change password
export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (passwordData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/change-password`,
        passwordData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            lang: currentLang,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to change password!";
      return rejectWithValue({ message });
    }
  }
);

// Get roles
export const getRoles = createAsyncThunk(
  "auth/getRoles",
  async (page = 1, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/admin/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          lang: currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get roles!";
      return rejectWithValue({ message });
    }
  }
);

// Get permissions
export const getPermissions = createAsyncThunk(
  "auth/getPermissions",
  async (page = 1, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/admin/permissions`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          lang: currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get permissions!";
      return rejectWithValue({ message });
    }
  }
);

// Create role
export const createRole = createAsyncThunk(
  "auth/createRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(`${BASE_URL}/admin/roles`, roleData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          lang: currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to create role!";
      return rejectWithValue({ message });
    }
  }
);

// Get role details
export const getRoleDetails = createAsyncThunk(
  "auth/getRoleDetails",
  async (roleId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const url = `${BASE_URL}/admin/roles/${roleId}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          lang: currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get role details!";
      return rejectWithValue({ message });
    }
  }
);

// Update role
export const updateRole = createAsyncThunk(
  "auth/updateRole",
  async ({ roleId, roleData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const url = `${BASE_URL}/admin/roles/${roleId}`;

      const response = await axios.post(url, roleData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          lang: currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update role!";
      return rejectWithValue({ message });
    }
  }
);

// Assign permissions to role
export const assignPermissionsToRole = createAsyncThunk(
  "auth/assignPermissionsToRole",
  async ({ roleId, permissions }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      // إرسال الصلاحيات كـ array مباشر
      const response = await axios.post(
        `${BASE_URL}/admin/roles/${roleId}/permissions`,
        {
          permissions: permissions,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            lang: currentLang,
          },
        }
      );

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to assign permissions!";
      return rejectWithValue({ message });
    }
  }
);

// Get all admins
export const getAllAdmins = createAsyncThunk(
  "auth/getAllAdmins",
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(
        `${BASE_URL}/admin/profile/show-all-profiles`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            lang: currentLang,
          },
        }
      );

      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get admins!";
      return rejectWithValue({ message });
    }
  }
);

// Assign role to admin
export const assignRoleToAdmin = createAsyncThunk(
  "auth/assignRoleToAdmin",
  async ({ adminId, roleId }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/admin/roles/${adminId}/assign-role`,
        {
          role_id: roleId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            lang: currentLang,
          },
        }
      );

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to assign role to admin!";
      return rejectWithValue({ message });
    }
  }
);

// Create admin
export const createAdmin = createAsyncThunk(
  "auth/createAdmin",
  async (adminData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(`${BASE_URL}/admin/admins`, adminData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
          lang: currentLang,
        },
      });

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to create admin!";
      return rejectWithValue({ message });
    }
  }
);

// Update admin
export const updateAdmin = createAsyncThunk(
  "auth/updateAdmin",
  async ({ adminId, adminData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/admin/admins/${adminId}`,
        adminData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            lang: currentLang,
          },
        }
      );

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update admin!";
      return rejectWithValue({ message });
    }
  }
);

// Delete admin
export const deleteAdmin = createAsyncThunk(
  "auth/deleteAdmin",
  async (adminId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(
        `${BASE_URL}/admin/admins/${adminId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            lang: currentLang,
          },
        }
      );

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete admin!";
      return rejectWithValue({ message });
    }
  }
);

// Toggle admin status
export const toggleAdminStatus = createAsyncThunk(
  "auth/toggleAdminStatus",
  async (adminId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/admin/admins/${adminId}/toggle-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
            lang: currentLang,
          },
        }
      );

      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to toggle admin status!";
      return rejectWithValue({ message });
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    profile: null,
    roles: [],
    permissions: [],
    admins: [],
    roleDetails: null,
    rolesPagination: null,
    permissionsPagination: null,
    isLoading: false,
    error: null,
    success: null,
    need2FA: false,
    twoFAEmail: null,
    twoFATempToken: null,
    twoFAData: null, // للـ QR code data
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
    logout: (state) => {
      state.user = null;
      state.error = null;
      state.success = null;
      state.need2FA = false;
      state.twoFAEmail = null;
      state.twoFATempToken = null;
      state.twoFAData = null;
      sessionStorage.removeItem("adminkhadamat");
    },
    clear2FA: (state) => {
      state.need2FA = false;
      state.twoFAEmail = null;
      state.twoFATempToken = null;
      state.twoFAData = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;

        // التحقق من 2FA
        if (
          action.payload.need_2fa === true ||
          action.payload.data?.need_2fa === true
        ) {
          state.need2FA = true;
          state.twoFAEmail = action.payload.email || action.payload.data?.email;
          state.twoFATempToken =
            action.payload.temp_token || action.payload.data?.temp_token;
          // حفظ temp data في sessionStorage
          sessionStorage.setItem(
            "admin2FATemp",
            JSON.stringify({
              email: state.twoFAEmail,
              temp_token: state.twoFATempToken,
            })
          );
          return; // لا نكمل عملية تسجيل الدخول
        }

        // التحقق إذا كانت البيانات محفوظة بالفعل في adminkhadamat (يعني المستخدم قرر المتابعة)
        const savedData = sessionStorage.getItem("adminkhadamat");
        if (
          savedData &&
          (action.payload.status === 200 || action.payload.statusCode === 200)
        ) {
          try {
            const savedParsed = JSON.parse(savedData);
            // إذا كانت نفس البيانات، يعني المستخدم اختار "متابعة بدون تفعيل"
            if (
              JSON.stringify(savedParsed) === JSON.stringify(action.payload)
            ) {
              // حفظ في Redux state
              state.user = action.payload;
              state.need2FA = false;
              state.twoFAEmail = null;
              state.twoFATempToken = null;
              return;
            }
          } catch {}
        }

        // تسجيل دخول عادي - لا نحفظ في sessionStorage أو Redux حتى يقرر المستخدم
        // سيتم الحفظ من الكومبوننت بعد اختيار المستخدم (متابعة بدون تفعيل أو بعد التحقق من 2FA)
        // نحفظ فقط في temp للتأكد من وجود البيانات
        if (
          action.payload.status === 200 ||
          action.payload.statusCode === 200
        ) {
          // لا نحفظ هنا - سيتم الحفظ من handleContinueWithout2FA أو verify2FA
          sessionStorage.setItem(
            "adminkhadamat_temp",
            JSON.stringify(action.payload)
          );
        }

        // لا نحدث state.user هنا حتى يقرر المستخدم
        state.need2FA = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Profile reducers
      .addCase(getUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload.data;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Update profile reducers
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload.data;
        state.success = action.payload.message;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Change password reducers
      .addCase(changePassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Roles reducers
      .addCase(getRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload.data;
        state.rolesPagination = null; // لا يوجد pagination في الـ response الجديد
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Permissions reducers
      .addCase(getPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload.data;
        state.permissionsPagination = null; // لا يوجد pagination في الـ response الجديد
      })
      .addCase(getPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Create role reducers
      .addCase(createRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Get role details reducers
      .addCase(getRoleDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRoleDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roleDetails = action.payload.data;
      })
      .addCase(getRoleDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Update role reducers
      .addCase(updateRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Delete role reducers
      .addCase(deleteRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Assign permissions reducers
      .addCase(assignPermissionsToRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignPermissionsToRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(assignPermissionsToRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Get all admins reducers
      .addCase(getAllAdmins.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllAdmins.fulfilled, (state, action) => {
        state.isLoading = false;
        state.admins = action.payload.data;
      })
      .addCase(getAllAdmins.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Assign role to admin reducers
      .addCase(assignRoleToAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(assignRoleToAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(assignRoleToAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Create admin reducers
      .addCase(createAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(createAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Update admin reducers
      .addCase(updateAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Delete admin reducers
      .addCase(deleteAdmin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAdmin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(deleteAdmin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Toggle admin status reducers
      .addCase(toggleAdminStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleAdminStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(toggleAdminStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Enable 2FA reducers
      .addCase(enable2FA.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(enable2FA.fulfilled, (state, action) => {
        state.isLoading = false;
        state.twoFAData = action.payload.data;
      })
      .addCase(enable2FA.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Verify 2FA reducers
      .addCase(verify2FA.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verify2FA.fulfilled, (state, action) => {
        state.isLoading = false;
        state.need2FA = false;
        state.twoFAEmail = null;
        state.twoFATempToken = null;
        state.twoFAData = null;
        state.user = action.payload;
        // حفظ البيانات بعد التحقق الناجح
        sessionStorage.setItem("adminkhadamat", JSON.stringify(action.payload));
        sessionStorage.removeItem("admin2FATemp");
        sessionStorage.removeItem("adminkhadamat_temp");
      })
      .addCase(verify2FA.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      });
  },
});

export const { clearState, logout, clear2FA } = authSlice.actions;
export default authSlice.reducer;
